import { apiFetchJson } from '../http/apiFetch';

/** Mirrors the backend `AdminSessionResponse` returned by `GET /api/admin/session`. */
export interface AdminSession {
  adminUserId: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  expiresAt: string;
}

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function normalizePermissions(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => asString(item).trim()).filter((item) => item !== '');
}

function normalizeSession(row: unknown): AdminSession | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const s = row as Record<string, unknown>;
  const email = asString(s.email ?? s.Email).trim();
  if (!email) {
    return null;
  }
  return {
    adminUserId: asString(s.adminUserId ?? s.AdminUserId).trim(),
    email,
    name: asString(s.name ?? s.Name).trim(),
    role: asString(s.role ?? s.Role ?? 'Admin').trim(),
    permissions: normalizePermissions(s.permissions ?? s.Permissions),
    expiresAt: asString(s.expiresAt ?? s.ExpiresAt),
  };
}

/**
 * Resolves the admin session for the signed-in user. The backend validates the Entra token AND
 * checks the `AdminAccessAllowlist` DB table (the whitelist). Returns the session when allowlisted,
 * or `null` on any failure (401/403/network), which callers treat as "not an admin".
 */
export async function fetchAdminSession(
  getAccessToken: () => Promise<string | undefined>
): Promise<AdminSession | null> {
  try {
    const result = await apiFetchJson<unknown>('/api/admin/session', {
      method: 'GET',
      getAccessToken,
    });
    return normalizeSession(result);
  } catch {
    return null;
  }
}
